import Series from '@/models/Series';
import PlayoffService from '@/services/PlayoffService';
import {
  CompetitionFormat,
  GameStage,
  TerminationForfeit,
  TeamMode,
  TeamPairingMode,
  RandomTeamFormationMode,
  TournamentStage,
  WinCondition,
} from '@/services/utils/enums';
import { buildRandomTeamRoundPairings } from '@/services/pairings/RandomTeamFormationService';
import GameFactory from '@/services/GameFactory';
import PairingHistoryService from '@/services/pairings/PairingHistoryService';
import { makeLogger } from '@/services/utils/logger';
import { translate } from '@/i18n';
import { LOGGING } from '@/config';

const DEBUG_LOG = true;
const log = makeLogger('EliminationService', DEBUG_LOG, LOGGING.MODELS_LOG);

class EliminationService {
  /**
   * Запускает плей-офф в данных турнира и создаёт стартовые игры.
   * Метод не сохраняет турнир и игры в БД; вызывающий код сам решает, когда сохранять.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Array|{topParticipants:Array,bottomParticipants:Array}} [orderedParticipants=[]]
   * @returns {Promise<Game[]>}
   */
  async start(tournament, orderedParticipants = []) {
    if (!tournament) {
      throw new Error(translate('serviceMessages.playoffTournamentRequired'));
    }

    const groups = PlayoffService.getGroups(tournament, orderedParticipants);
    PlayoffService.assertGroups(tournament, groups);
    const { topParticipants, bottomParticipants } = groups;
    // Prepare both brackets before mutating the tournament.
    const topPairs = await this.pairParticipants(tournament, topParticipants);
    const bottomPairs = bottomParticipants.length ? await this.pairParticipants(tournament, bottomParticipants) : [];
    tournament.stage = TournamentStage.ELIMINATION;
    const newGames = [];

    newGames.push(...this.createGames(tournament, topPairs, { isTop: true, level: 1 }));

    if (bottomParticipants.length) {
      newGames.push(...this.createGames(tournament, bottomPairs, { isTop: false, level: 1 }));
    }

    return newGames;
  }

  /**
   * Создаёт Game[] плей-офф из списка участников.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Array} participants
   * @param {boolean} [isTop=true]
   * @param {boolean} [isLoos=false]
   * @param {number} [level=1]
   * @returns {Promise<Game[]>}
   */
  async create(tournament, participants, isTop = true, isLoos = false, level = 1) {
    const pairs = await this.pairParticipants(tournament, participants);
    return this.createGames(tournament, pairs, { isTop, isLoos, level });
  }

  /**
   * Подготавливает пары плей-офф из участников без создания Game.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Array} participants
   * @returns {Promise<Array<Object>>}
   */
  async pairParticipants(tournament, participants) {
    if (tournament.isTeamTournament() && tournament.teamSettings?.teamMode === TeamMode.RANDOM) {
      return this.#pairRandomTeams(tournament, participants);
    }

    const pairs = await PlayoffService.pairSeeds(participants);

    if (tournament.isTeamTournament() && tournament.teamSettings?.teamMode === TeamMode.FIXED) {
      return pairs.map((pair) => ({
        ...pair,
        pairKind: 'team-fixed',
        eliminationParticipantCount: pair.eliminationParticipantCount,
      }));
    }

    return pairs.map((pair) => ({
      ...pair,
      pairKind: 'individual',
      eliminationParticipantCount: pair.eliminationParticipantCount,
    }));
  }

  /**
   * Создаёт Game[] плей-офф из уже подготовленных пар.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Array<Object>} pairs
   * @param {Object} [options={}]
   * @returns {Game[]}
   */
  createGames(tournament, pairs, {
    isTop = true,
    isLoos = false,
    level = 1,
    stage = null,
    stageNumber = null,
  } = {}) {
    if (!Array.isArray(pairs)) {
      throw new Error(translate('serviceMessages.playoffPairsRequired'));
    }

    const participantCount = pairs[0]?.eliminationParticipantCount ?? pairs.length * 2;
    const nextStage = stage ?? getStage(participantCount, isTop, isLoos);
    const nextStageNumber = stageNumber ?? this.getStageNumber(tournament, isTop, nextStage);
    const pairKind = this.#getPairKind(tournament, pairs[0]);

    if (pairKind === 'individual') {
      log('createGames', 'Создание индивидуальных матчей:', { stage: nextStage, pairs });
      const preparedPairs = pairs.map((pair) => ({
        ...pair,
        seriesRound: pair.isBye ? 1 : this.getStageGameCountBetweenSides(tournament, nextStage, pair.side1.id, pair.side2.id) + 1,
      }));
      const games = GameFactory.createIndividualEliminationGames(tournament, nextStage, preparedPairs, nextStageNumber, level, {
        startBoard: this.getFreeBoardNumber(tournament, level),
      });
      log('createGames', 'Созданы индивидуальные матчи:', { stage: nextStage, games });
      return games;
    }

    const newGames = [];
    let nextNumber = tournament.getLastGameNumber();
    let nextBoard = this.getFreeBoardNumber(tournament, level);
    const playerHistory = PairingHistoryService.buildPlayerHistory(tournament);

    for (const pair of pairs) {
      const generationPair = pairKind === 'team-random'
        ? {
            ...pair,
            side1: normalizeRandomTeamSide(pair?.side1),
            side2: normalizeRandomTeamSide(pair?.side2),
          }
        : {
            side1Id: pair?.side1?.id,
            side2Id: pair?.side2?.id,
            isBye: !pair?.side2?.id,
          };
      const side1Id = pairKind === 'team-random'
        ? generationPair.side1?.key
        : generationPair.side1Id;
      const side2Id = pairKind === 'team-random'
        ? generationPair.side2?.key
        : generationPair.side2Id;
      const seriesRound = this.getStageTeamMatchCountBetweenSides(tournament, nextStage, side1Id, side2Id) + 1;

      const createOptions = {
        round: seriesRound,
        cycle: null,
        startNumber: nextNumber,
        startBoard: nextBoard,
        playerStandings: [],
        playerHistory,
        mirrorRepeatPairColors: false,
        stage: nextStage,
        stageNumber: nextStageNumber,
        level,
        bracketIndex: pair?.bracketIndex ?? null,
      };
      const { games, nextNumberValue, nextBoardValue } = pairKind === 'team-random'
        ? GameFactory.createRandomTeamMatch(tournament, generationPair, createOptions)
        : GameFactory.createFixedTeamMatch(tournament, generationPair, createOptions);

      nextNumber = nextNumberValue;
      if (!pair.isBye) nextBoard = nextBoardValue;
      if (pair.isBye) {
        games.forEach(game => {
          game.board = null;
          game.result = null;
          game.termination = null;
          game.resultSetAt = null;
          game.rated = false;
        });
      }
      games.forEach((game) => tournament.addGame(game));
      newGames.push(...games);
    }

    return newGames;
  }

  /**
   * Генерирует порядок индексов для визуальной сетки плей-офф.
   * Метод только рассчитывает порядок, не меняет турнир и не сохраняет данные.
   * @param {number} count
   * @returns {Array<number|null>}
   */
  generateBracketOrder(count) {
    const participants = Array.from({ length: count }, (_value, index) => index + 1);
    const bracket = this.#buildBracket(participants);

    log('generateBracketOrder', '🧩 Generated bracket order:', bracket);

    return bracket.flat();
  }

  /**
   * Формирует пары посева для визуального порядка сетки.
   * @param {Array<number>} participants
   * @returns {Array<Array<number|null>>}
   */
  #buildBracket(participants) {
    const participantsCount = participants.length;
    const rounds = Math.ceil(Math.log(participantsCount) / Math.log(2));
    const bracketSize = Math.pow(2, rounds);
    const requiredByes = bracketSize - participantsCount;

    log('buildBracket', '👥 Number of participants:', participantsCount);
    log('buildBracket', '🔁 Number of rounds:', rounds);
    log('buildBracket', '📐 Bracket size:', bracketSize);
    log('buildBracket', '📭 Required number of byes:', requiredByes);

    if (participantsCount < 2) {
      return [];
    }

    let games = [[1, 2]];

    for (let round = 1; round < rounds; round++) {
      const roundGames = [];
      const sum = Math.pow(2, round + 1) + 1;

      for (let i = 0; i < games.length; i++) {
        let home = this.#changeSeedIntoBye(games[i][0], participantsCount);
        let away = this.#changeSeedIntoBye(sum - games[i][0], participantsCount);
        roundGames.push([home, away]);
        home = this.#changeSeedIntoBye(sum - games[i][1], participantsCount);
        away = this.#changeSeedIntoBye(games[i][1], participantsCount);
        roundGames.push([home, away]);
      }

      games = roundGames;
    }

    return games;
  }

  /**
   * Возвращает null для seed, который выходит за пределы числа участников.
   * @param {number} seed
   * @param {number} participantsCount
   * @returns {number|null}
   */
  #changeSeedIntoBye(seed, participantsCount) {
    return seed <= participantsCount ? seed : null;
  }

  /**
   * Формирует пары временных random-команд для плей-офф.
   * На вход получает игроков, внутри использует тот же сервис формирования random-команд,
   * что и регулярная стадия, но возвращает уже пары сторон для создания игр плей-офф.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Array} participants
   * @returns {Promise<Array<Object>>}
   */
  async #pairRandomTeams(tournament, participants) {
    const players = Array.isArray(participants)
      ? participants
          .map((participant) => participant?.entity ?? participant)
          .filter((player) => player?.id)
      : [];
    const issue = PlayoffService.getGroupIssue(tournament, players);
    if (issue) throw Object.assign(new Error(issue.message), { code: 'PLAYOFF_VALIDATION' });
    const teamSize = tournament.getTeamSize() ?? 2;
    const bracketSize = 2 ** Math.ceil(Math.log2(players.length / teamSize));
    const byeTeamCount = bracketSize - players.length / teamSize;
    // The highest seeds advance as complete teams; only the remaining players are paired.
    const byePlayers = players.slice(0, byeTeamCount * teamSize);
    const playingPlayers = players.slice(byePlayers.length);
    const playerStandings = await tournament.calculateStandings();
    const pairingMode = tournament.teamSettings?.teamPairingMode ?? TeamPairingMode.AUTO;
    const pastTeamMatches = tournament.getTeamGames(null, 'elimination');
    const playerIds = new Set(players.map(player => player.id));
    const relevantMatches = pastTeamMatches.filter(match => !match.isByeGroup && !match.games.every(game => game.isBye) &&
      [...(match.side1?.members || []), ...(match.side2?.members || [])].some(player => playerIds.has(player.id)));
    const levelOf = match => Math.max(0, ...match.games.map(game => game.level ?? game.stageNumber ?? 0));
    const previousLevel = Math.max(0, ...relevantMatches.map(levelOf));
    const previousTeamLineups = relevantMatches.filter(match => levelOf(match) === previousLevel)
      .flatMap(match => [match.side1?.members || [], match.side2?.members || []])
      .map(members => members.map(player => player.id));
    const { pairs: teamPairs } = buildRandomTeamRoundPairings({
      players: playingPlayers,
      teamSize,
      round: tournament.getEliminationGames().length + 1,
      cycle: null,
      playerStandings,
      pastTeamMatches: relevantMatches,
      previousTeamLineups,
      balanceByRating: true,
      mode: tournament.teamSettings?.randomTeamFormationMode ?? RandomTeamFormationMode.RANDOM,
      useBoardOpponents: pairingMode !== TeamPairingMode.MANUAL && !tournament.shouldUsePairingColors(),
      commonBoard: pairingMode !== TeamPairingMode.MANUAL && tournament.shouldUsePairingColors(),
      playerHistory: PairingHistoryService.buildPlayerHistory(tournament, 'elimination'),
      controlType: tournament.getControlType(),
    });
    const byePairs = [];
    for (let index = 0; index < byePlayers.length; index += teamSize) {
      const members = byePlayers.slice(index, index + teamSize);
      const side1 = { key: `playoff-bye-${members.map(player => player.id).join('-')}`, players: members, name: members.map(player => player.name).join(' / ') };
      byePairs.push({ side1, side2: null, isBye: true });
    }

    const sortedTeamPairs = [...teamPairs].sort((a, b) => {
      if (Boolean(a?.isBye) !== Boolean(b?.isBye)) return a?.isBye ? 1 : -1;
      return (a?.board ?? 0) - (b?.board ?? 0);
    });

    return [...byePairs, ...sortedTeamPairs].map((pair, index) => ({
      ...pair,
      pairKind: 'team-random',
      eliminationParticipantCount: bracketSize,
      bracketIndex: index + 1,
    }));
  }

  /**
   * Определяет тип подготовленной пары: индивидуальная, fixed-team или random-team.
   * Если тип уже записан в pairKind, доверяем ему; иначе выводим из настроек турнира.
   * @param {import('@/models/Tournament').default} tournament
   * @param {Object|null} pair
   * @returns {'individual'|'team-fixed'|'team-random'}
   */
  #getPairKind(tournament, pair) {
    if (pair?.pairKind) return pair.pairKind;
    if (!tournament.isTeamTournament()) return 'individual';
    if (tournament.teamSettings?.teamMode === TeamMode.RANDOM) return 'team-random';
    if (tournament.teamSettings?.teamMode === TeamMode.FIXED) return 'team-fixed';
    return 'individual';
  }

  /**
   * Возвращает номер стадии плей-офф для указанной сетки и стадии.
   * @param {import('@/models/Tournament').default} tournament
   * @param {boolean} [isTop=true]
   * @param {string|null} [stage=null]
   * @returns {number}
   */
  getStageNumber(tournament, isTop = true, stage = null) {
    const stagePairs = new Map([
      [GameStage.F, GameStage.P3],
      [GameStage.P3, GameStage.F],
      [GameStage.LF, GameStage.LP3],
      [GameStage.LP3, GameStage.LF],
    ]);

    const games = isTop
      ? tournament.games.elimination.top || []
      : tournament.games.elimination.bottom || [];

    let result;
    if (stage && stagePairs.has(stage)) {
      const siblingStage = stagePairs.get(stage);
      const siblingGame = games.find((game) => game.stage === siblingStage);
      if (siblingGame?.stageNumber) {
        result = siblingGame.stageNumber;
      }
    }

    if (result === undefined) {
      result = Math.max(0, ...games.map((game) => game.stageNumber || 0)) + 1;
    }

    return result;
  }

  /**
   * Возвращает следующий свободный номер доски для уровня плей-офф.
   * @param {import('@/models/Tournament').default} tournament
   * @param {number} level
   * @returns {number}
   */
  getFreeBoardNumber(tournament, level) {
    const boardsInStageAndRound = [
      ...tournament.games.elimination.top,
      ...tournament.games.elimination.bottom,
    ]
      .filter((game) => game.level === level && !game.isBye)
      .map((game) => game.board || 0);

    const board = Math.max(0, ...boardsInStageAndRound) + 1;
    return board || 0;
  }

  /**
   * Возвращает количество матчей между двумя сторонами на указанной стадии.
   * Это часть логики серии плей-офф, а не фабрики создания Game.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string} stage
   * @param {string} side1Id
   * @param {string} side2Id
   * @returns {number}
   */
  getStageGameCountBetweenSides(tournament, stage, side1Id, side2Id) {
    return tournament.getEliminationGames().filter((game) =>
      game.stage === stage &&
      ((game?.sideHasParticipant?.('white', side1Id) && game?.sideHasParticipant?.('black', side2Id)) ||
       (game?.sideHasParticipant?.('white', side2Id) && game?.sideHasParticipant?.('black', side1Id)))
    ).length;
  }

  /**
   * Возвращает количество командных матчей между двумя сторонами на указанной стадии.
   * Используется для нумерации партии серии плей-офф по той же схеме, что и
   * `getStageGameCountBetweenSides` в индивидуальном режиме.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string} stage
   * @param {string|null} side1Id
   * @param {string|null} side2Id
   * @returns {number}
   */
  getStageTeamMatchCountBetweenSides(tournament, stage, side1Id, side2Id) {
    if (!side1Id || !side2Id) return 0;

    return tournament.getTeamMatches(tournament.getEliminationGames()).filter((teamMatch) =>
      teamMatch.stage === stage &&
      ((teamMatch.side1?.id === side1Id && teamMatch.side2?.id === side2Id) ||
       (teamMatch.side1?.id === side2Id && teamMatch.side2?.id === side1Id))
    ).length;
  }

  /**
   * Возвращает серии матчей плей-офф для указанной стадии или всех стадий.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string|null} [stage=null]
   * @returns {Promise<Series[]>}
   */
  async getSeries(tournament, stage = null) {
    if (tournament.isTeamTournament()) {
      return this.#getTeamSeries(tournament, stage);
    }

    const allGames = stage
      ? tournament.getEliminationGames().filter((game) => game.stage === stage)
      : tournament.getEliminationGames();
    const elimSettings = tournament.getEliminationSettings() || {};
    const winCondition = elimSettings.winCondition ?? 'score';
    const countToWin = elimSettings.countToWin ?? 1;
    const pairs = {};
    const seriesSideIdsByKey = {};
    const standings = await tournament.calculateStandings();

    for (const game of allGames) {
      const whitePlayerId = game?.getSingleWhitePlayerId?.();
      const blackPlayerId = game?.getSingleBlackPlayerId?.();
      if (game.isBye && (whitePlayerId || blackPlayerId)) {
        const participantId = game.getSideParticipantId(whitePlayerId ? 'white' : 'black');
        pairs[`bye-${game.stage}-${participantId}`] = new Series({
          side1: this.#buildParticipantSeriesSide(tournament, participantId),
          side2: {}, items: [game], winCondition, countToWin,
          stage: game.stage, stageNumber: game.stageNumber, bracketIndex: game.bracketIndex,
          meta: { automaticAdvance: true },
        });
        continue;
      }
      if (!whitePlayerId || !blackPlayerId) continue;
      const whiteSideParticipantId = game?.getSideParticipantId?.('white') ?? null;
      const blackSideParticipantId = game?.getSideParticipantId?.('black') ?? null;
      if (!whiteSideParticipantId || !blackSideParticipantId) continue;

      const key = [game.stage, whiteSideParticipantId, blackSideParticipantId].sort().join('-');
      const seriesStage = game.stage;
      const stageNumber = game.stageNumber ?? 1;
      if (!pairs[key]) {
        const [id1, id2] = [whiteSideParticipantId, blackSideParticipantId];
        const index1 = standings.findIndex((player) => player.id === id1);
        const index2 = standings.findIndex((player) => player.id === id2);
        const firstId = index1 < index2 ? id1 : id2;
        const secondId = index1 < index2 ? id2 : id1;
        const side1 = this.#buildParticipantSeriesSide(tournament, firstId);
        const side2 = this.#buildParticipantSeriesSide(tournament, secondId);
        pairs[key] = new Series({
          seriesType: CompetitionFormat.INDIVIDUAL,
          side1,
          side2,
          items: [],
          winCondition,
          countToWin,
          stage: seriesStage,
          label: stage
            ? translate(`playoffStages.${stage}`)
            : translate('eliminationPairings.playoff'),
          stageNumber,
          bracketIndex: game.bracketIndex,
        });
        seriesSideIdsByKey[key] = { side1Id: firstId, side2Id: secondId };
      }
      pairs[key].items.push(game);
      this.#recordSeriesPlayedEntity(pairs[key], 'side1', game, seriesSideIdsByKey[key]?.side1Id, tournament);
      this.#recordSeriesPlayedEntity(pairs[key], 'side2', game, seriesSideIdsByKey[key]?.side2Id, tournament);
    }

    return Object.values(pairs);
  }

  #buildParticipantSeriesSide(tournament, participantId) {
    const participant = tournament.getParticipant?.(participantId) ?? null;
    const entity = participant?.entity
      ?? tournament.players?.find((player) => player?.id === participantId)
      ?? null;
    const entityIds = typeof tournament.getParticipantEntityIds === 'function'
      ? tournament.getParticipantEntityIds(participantId)
      : [participantId];

    return {
      entityType: 'player',
      id: participant?.id ?? participantId ?? null,
      name: entity?.name ?? '',
      members: entity ? [entity] : [],
      entity,
      meta: {
        participantId: participant?.id ?? participantId ?? null,
        entityIds,
        displayEntity: entity ?? null,
        playedEntities: [],
        replacements: participant?.replacements ?? [],
      },
    };
  }

  #recordSeriesPlayedEntity(series, sideKey, game, participantId, tournament) {
    const side = series?.[sideKey] ?? null;
    if (!side || !participantId || !game) return;

    const playerId = game?.sideHasParticipant?.('white', participantId)
      ? game?.getSingleWhitePlayerId?.()
      : (game?.sideHasParticipant?.('black', participantId) ? game?.getSingleBlackPlayerId?.() : null);
    if (!playerId) return;

    const player = tournament.getPlayerById?.(playerId)
      ?? tournament.players?.find((item) => item?.id === playerId)
      ?? null;
    if (!player) return;

    const playedEntities = Array.isArray(side.meta?.playedEntities) ? side.meta.playedEntities : [];
    if (!playedEntities.some((item) => item?.id === player.id)) {
      playedEntities.push(player);
    }

    side.meta = {
      ...(side.meta || {}),
      displayEntity: player,
      playedEntities,
    };
  }

  /**
   * Рассчитывает итоговые места после плей-офф по последней сыгранной стадии участника.
   * Участники одной стадии сортируются по результату последней серии, затем по месту regular.
   * @param {Object} params
   * @param {Array<Object>} params.regularStandings
   * @param {Series[]} params.eliminationSeries
   * @param {string[]} [params.stageOrder]
   * @returns {Object<string, number>}
   */
  calculateOverallPlaces({ regularStandings = [], eliminationSeries = [], stageOrder = null } = {}) {
    const regularRows = Array.isArray(regularStandings) ? regularStandings : [];
    const seriesList = Array.isArray(eliminationSeries) ? eliminationSeries : [];
    const order = this.#buildOverallStageOrder(stageOrder);
    const stageRankByCode = new Map(order.map((stage, index) => [stage, index]));
    const regularPlaceById = {};
    const participantIdByEntityId = {};

    regularRows.forEach((row, index) => {
      if (row?.id) regularPlaceById[row.id] = index + 1;
      this.#getRegularRowEntityIds(row).forEach((entityId) => {
        if (row?.id && entityId) participantIdByEntityId[entityId] = row.id;
      });
    });

    const recordsById = new Map();
    const finalPlaceByParticipantId = new Map();

    const ensureRecord = (participantId) => {
      if (!participantId) return null;
      if (!recordsById.has(participantId)) {
        recordsById.set(participantId, {
          participantId,
          stage: null,
          stageRank: -1,
          resultRank: 0,
          regularPlace: regularPlaceById[participantId] ?? Number.MAX_SAFE_INTEGER,
        });
      }
      return recordsById.get(participantId);
    };

    Object.keys(regularPlaceById).forEach(ensureRecord);

    for (const series of seriesList) {
      const stage = series?.stage ?? null;
      const stageRank = stageRankByCode.has(stage) ? stageRankByCode.get(stage) : -1;
      if (stageRank < 0) continue;

      const score = typeof series?.getScore === 'function' ? series.getScore() : null;
      const side1Id = series?.side1?.id ?? null;
      const side2Id = series?.side2?.id ?? null;
      if (!score || !side1Id || !side2Id) continue;

      const side1Score = Number(score[side1Id] ?? 0);
      const side2Score = Number(score[side2Id] ?? 0);
      const side1ResultRank = side1Score > side2Score ? 1 : 0;
      const side2ResultRank = side2Score > side1Score ? 1 : 0;
      const side1ParticipantIds = this.#getSeriesSideParticipantIds(series, 'side1', regularPlaceById, participantIdByEntityId);
      const side2ParticipantIds = this.#getSeriesSideParticipantIds(series, 'side2', regularPlaceById, participantIdByEntityId);

      side1ParticipantIds.forEach((participantId) => {
        this.#updateOverallPlaceRecord(ensureRecord(participantId), stage, stageRank, side1ResultRank, side1ParticipantIds);
      });
      side2ParticipantIds.forEach((participantId) => {
        this.#updateOverallPlaceRecord(ensureRecord(participantId), stage, stageRank, side2ResultRank, side2ParticipantIds);
      });

      this.#applyFinalSeriesPlaces({
        stage,
        side1Score,
        side2Score,
        side1ParticipantIds,
        side2ParticipantIds,
        finalPlaceByParticipantId,
      });
    }

    const occupiedPlaces = new Set(finalPlaceByParticipantId.values());
    const groupPlaces = new Map();
    let nextPlace = 1;

    return Array.from(recordsById.values())
      .sort((a, b) => {
        if (b.stageRank !== a.stageRank) return b.stageRank - a.stageRank;
        if (b.resultRank !== a.resultRank) return b.resultRank - a.resultRank;
        return a.regularPlace - b.regularPlace;
      })
      .reduce((acc, record) => {
        if (finalPlaceByParticipantId.has(record.participantId)) {
          acc[record.participantId] = finalPlaceByParticipantId.get(record.participantId);
          return acc;
        }

        const groupKey = record.groupKey || record.participantId;
        if (groupPlaces.has(groupKey)) {
          acc[record.participantId] = groupPlaces.get(groupKey);
          return acc;
        }
        while (occupiedPlaces.has(nextPlace)) nextPlace++;
        acc[record.participantId] = nextPlace;
        groupPlaces.set(groupKey, nextPlace);
        nextPlace++;
        return acc;
      }, {});
  }

  /**
   * Возвращает id физических сущностей, связанных со строкой standings.
   * @param {Object|null} row
   * @returns {string[]}
   */
  #getRegularRowEntityIds(row) {
    const ids = [
      row?.entity?.id,
      row?.participant?.currentEntityId,
      ...(Array.isArray(row?.participant?.replacements)
        ? row.participant.replacements.map((replacement) => replacement?.fromEntityId)
        : []),
      ...(Array.isArray(row?.replacements)
        ? row.replacements.map((replacement) => replacement?.fromEntityId)
        : []),
    ];

    return Array.from(new Set(ids.filter(Boolean)));
  }

  /**
   * Присваивает командные места финальным сериям: F => 1/2, P3 => 3/4.
   * @param {Object} params
   * @returns {void}
   */
  #applyFinalSeriesPlaces({
    stage,
    side1Score,
    side2Score,
    side1ParticipantIds = [],
    side2ParticipantIds = [],
    finalPlaceByParticipantId,
  } = {}) {
    const placesByStage = {
      [GameStage.F]: { winner: 1, loser: 2 },
      [GameStage.P3]: { winner: 3, loser: 4 },
    };
    const places = placesByStage[stage];
    if (!places || side1Score === side2Score) return;

    const winnerIds = side1Score > side2Score ? side1ParticipantIds : side2ParticipantIds;
    const loserIds = side1Score > side2Score ? side2ParticipantIds : side1ParticipantIds;

    winnerIds.forEach((participantId) => {
      if (participantId) finalPlaceByParticipantId.set(participantId, places.winner);
    });
    loserIds.forEach((participantId) => {
      if (participantId) finalPlaceByParticipantId.set(participantId, places.loser);
    });
  }

  /**
   * Возвращает id стороны серии и id её участников.
   * @param {Series} series
   * @param {'side1'|'side2'} sideKey
   * @param {Object<string, number>} regularPlaceById
   * @param {Object<string, string>} participantIdByEntityId
   * @returns {string[]}
   */
  #getSeriesSideParticipantIds(series, sideKey, regularPlaceById = {}, participantIdByEntityId = {}) {
    const side = sideKey === 'side2' ? series?.side2 : series?.side1;
    if (!side) return [];

    if (side.id && Object.prototype.hasOwnProperty.call(regularPlaceById, side.id)) {
      return [side.id];
    }

    const ids = (Array.isArray(side.members) ? side.members : [])
      .map((member) => {
        const memberId = member?.id ?? null;
        if (!memberId) return null;
        if (Object.prototype.hasOwnProperty.call(regularPlaceById, memberId)) return memberId;
        return participantIdByEntityId[memberId] ?? null;
      })
      .filter((id) => id && Object.prototype.hasOwnProperty.call(regularPlaceById, id));

    return Array.from(new Set(ids));
  }

  /**
   * Обновляет запись участника, если найденная стадия является более поздней.
   * @param {Object|null} record
   * @param {string} stage
   * @param {number} stageRank
   * @param {number} resultRank
   * @returns {void}
   */
  #updateOverallPlaceRecord(record, stage, stageRank, resultRank, participantIds = []) {
    if (!record) return;
    if (stageRank < record.stageRank) return;

    if (stageRank > record.stageRank || resultRank > record.resultRank) {
      record.stage = stage;
      record.stageRank = stageRank;
      record.resultRank = resultRank;
      record.groupKey = JSON.stringify([...participantIds].sort());
    }
  }

  /**
   * Возвращает порядок стадий для итоговых мест: нижняя сетка ниже основной.
   * @param {string[]|null} stageOrder
   * @returns {string[]}
   */
  #buildOverallStageOrder(stageOrder) {
    const topOrder = Array.isArray(stageOrder) && stageOrder.length
      ? stageOrder
      : [GameStage.R64, GameStage.R32, GameStage.R16, GameStage.R8, GameStage.QF, GameStage.SF, GameStage.P3, GameStage.F];

    const lowerOrder = [
      GameStage.LR64,
      GameStage.LR32,
      GameStage.LR16,
      GameStage.LR8,
      GameStage.LQF,
      GameStage.LSF,
      GameStage.LP3,
      GameStage.LF,
    ];

    return [
      ...lowerOrder,
      ...topOrder,
    ].filter(Boolean);
  }

  /**
   * Собирает серии командного плей-офф из агрегированных TeamMatch.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string|null} [stage=null]
   * @returns {Promise<Series[]>}
   */
  async #getTeamSeries(tournament, stage = null) {
    const allTeamMatches = tournament.getTeamMatches(
      stage
        ? tournament.getEliminationGames().filter((game) => game.stage === stage)
        : tournament.getEliminationGames()
    );

    const elimSettings = tournament.getEliminationSettings() || {};
    const winCondition = elimSettings.winCondition ?? WinCondition.SCORE;
    const countToWin = elimSettings.countToWin ?? 1;
    const teamScoringUnit = elimSettings.teamScoringUnit ?? null;
    const pairs = {};

    for (const teamMatch of allTeamMatches) {
      if (teamMatch.isTeamBye && teamMatch.games.every(game => game.isBye)) {
        pairs[`bye-${teamMatch.teamMatchKey}`] = new Series({
          seriesType: CompetitionFormat.TEAM,
          side1: teamMatch.side1?.members?.length ? teamMatch.side1 : teamMatch.side2,
          side2: {}, items: [teamMatch], winCondition, countToWin, teamScoringUnit,
          stage: teamMatch.stage, stageNumber: teamMatch.stageNumber, bracketIndex: teamMatch.bracketIndex,
          meta: { automaticAdvance: true },
        });
        continue;
      }
      if (!teamMatch?.side1?.id || !teamMatch?.side2?.id) continue;

      const key = [teamMatch.stage, teamMatch.side1.id, teamMatch.side2.id].sort().join('-');

      if (!pairs[key]) {
        pairs[key] = new Series({
          seriesType: CompetitionFormat.TEAM,
          side1: teamMatch.side1,
          side2: teamMatch.side2,
          items: [],
          winCondition,
          countToWin,
          teamScoringUnit,
          stage: teamMatch.stage,
          label: teamMatch.stage
            ? translate(`playoffStages.${teamMatch.stage}`)
            : translate('eliminationPairings.playoff'),
          stageNumber: teamMatch.stageNumber,
          bracketIndex: teamMatch.bracketIndex,
          meta: {
            source: 'team-match',
          },
        });
      }

      this.#updateTeamSeriesSideFromMatch(pairs[key], teamMatch.side1);
      this.#updateTeamSeriesSideFromMatch(pairs[key], teamMatch.side2);
      pairs[key].items.push(teamMatch);
    }

    return Object.values(pairs);
  }

  /**
   * Обновляет сторону командной серии последним TeamMatch с тем же id.
   * В random-team плей-офф состав стороны может измениться из-за замен между
   * партиями серии, а сетка и следующий раунд должны использовать последний
   * реально игравший состав.
   * @param {Series} series
   * @param {Object|null} matchSide
   * @returns {void}
   */
  #updateTeamSeriesSideFromMatch(series, matchSide) {
    if (!series || !matchSide?.id) return;

    if (series.side1?.id === matchSide.id) {
      series.side1 = matchSide;
    } else if (series.side2?.id === matchSide.id) {
      series.side2 = matchSide;
    }
  }

  /**
   * Удаляет матчи указанной стадии плей-офф.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string} stage
   * @param {boolean} [isTop=true]
   * @param {number} [round=0]
   * @returns {void}
   */
  removeStage(tournament, stage, isTop = true, round = 0) {
    const target = isTop ? tournament.games.elimination.top : tournament.games.elimination.bottom;
    const filtered = target.filter((game) => {
      if (game.stage !== stage) return true;
      if (round && game.round !== round) return true;
      return false;
    });

    if (isTop) {
      tournament.games.elimination.top = filtered;
    } else {
      tournament.games.elimination.bottom = filtered;
    }
  }

  /**
   * Проверяет наличие обязательных финалов плей-офф.
   * @param {import('@/models/Tournament').default} tournament
   * @returns {boolean}
   */
  checkComplete(tournament) {
    const allElimination = tournament.getEliminationGames();
    const hasTopFinal = allElimination.some((game) => game.stage === GameStage.F);
    const elimSettings = tournament.getEliminationSettings();

    if (elimSettings?.bracketsType === 'top-bottom') {
      const hasBottomFinal = allElimination.some((game) => game.stage === GameStage.LF);
      return hasTopFinal && hasBottomFinal;
    }

    return hasTopFinal;
  }

  /**
   * Проверяет, завершены ли все партии плей-офф.
   * @param {import('@/models/Tournament').default} tournament
   * @returns {{totalCount:number, finishedCount:number, unfinishedCount:number, unfinished:string[], isComplete:boolean}}
   */
  checkGamesFinished(tournament) {
    const elimination = tournament.getEliminationGames();
    const isFinished = (game) => Boolean(game?.result) || Boolean(game?.isBye) || TerminationForfeit.includes(game?.termination);
    const unfinishedIds = elimination.filter((game) => !isFinished(game)).map((game) => game.id);
    const totalCount = elimination.length;
    const finishedCount = elimination.length - unfinishedIds.length;
    return {
      totalCount,
      finishedCount,
      unfinishedCount: unfinishedIds.length,
      unfinished: unfinishedIds,
      isComplete: unfinishedIds.length === 0,
    };
  }

  /**
   * Проверяет, определены ли победители во всех сериях плей-офф.
   * @param {import('@/models/Tournament').default} tournament
   * @returns {Promise<{totalCount:number, finishedCount:number, unfinishedCount:number, unfinished:Array, isComplete:boolean}>}
   */
  async checkHasWinners(tournament) {
    const allSeries = await this.getSeries(tournament);
    const unfinished = [];
    let finishedCount = 0;

    for (const series of allSeries) {
      const winner = series.getWinner?.();
      if (winner) {
        finishedCount += 1;
      } else {
        unfinished.push(series);
      }
    }

    const totalCount = allSeries.length;
    const unfinishedCount = unfinished.length;
    return {
      totalCount,
      finishedCount,
      unfinishedCount,
      unfinished,
      isComplete: totalCount === finishedCount,
    };
  }
}

/**
 * Приводит сторону временной random-команды к формату,
 * который ожидает создание командных игр.
 * @param {Object|null} side
 * @returns {Object|null}
 */
function normalizeRandomTeamSide(side) {
  if (!side) return null;

  return {
    ...side,
    key: side?.key ?? side?.id ?? null,
    name: side?.name ?? '',
    players: Array.isArray(side?.players)
      ? side.players
      : (Array.isArray(side?.members) ? side.members : []),
  };
}

/**
 * Возвращает стадию плей-офф по размеру сетки и ветке.
 * @param {number} count
 * @param {boolean} isTop
 * @param {boolean} [isLoos=false]
 * @returns {string}
 */
function getStage(count, isTop, isLoos = false) {
  const stageMap = {
    2:   isLoos ? (isTop ? GameStage.P3 : GameStage.LP3) : (isTop ? GameStage.F : GameStage.LF),
    4:   isTop ? GameStage.SF : GameStage.LSF,
    8:   isTop ? GameStage.QF : GameStage.LQF,
    16:  isTop ? GameStage.R8 : GameStage.LR8,
    32:  isTop ? GameStage.R16 : GameStage.LR16,
    64:  isTop ? GameStage.R32 : GameStage.LR32,
    128: isTop ? GameStage.R64 : GameStage.LR64,
  };
  return stageMap[count] || GameStage.REGULAR;
}

export default new EliminationService();
